import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ListAltIcon from '@mui/icons-material/ListAlt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useAuth } from '../../contexts/AuthContext';
import { shJson } from './api';
import { NearMissDistributionCharts } from './SafetyDistributionCharts';
import SafetyHuddleOptionListsDialog from './SafetyHuddleOptionListsDialog';

const NM_STATUS_OPTIONS = [
  { value: 'aberto', label: 'Aberto' },
  { value: 'em_analise', label: 'Em análise' },
  { value: 'resolvido', label: 'Resolvido' },
];

function statusChipColor(status) {
  if (status === 'resolvido') return 'success';
  if (status === 'em_analise') return 'info';
  return 'warning';
}

function daysOpenSince(createdAt, status) {
  if (!createdAt || status === 'resolvido') return null;
  const start = new Date(createdAt);
  if (Number.isNaN(start.getTime())) return null;
  const ms = Date.now() - start.getTime();
  return Math.max(0, Math.floor(ms / (24 * 60 * 60 * 1000)));
}

function daysOpenLabel(days) {
  if (days == null) return null;
  if (days === 0) return 'hoje';
  if (days === 1) return '1 dia em aberto';
  return `${days} dias em aberto`;
}

export default function SafetyHuddleSectorPage() {
  const { sectorId } = useParams();
  const navigate = useNavigate();
  const { unidadeSelecionada } = useAuth();

  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [sector, setSector] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [canManageForms, setCanManageForms] = useState(false);
  const [nearMisses, setNearMisses] = useState([]);

  const [openTpl, setOpenTpl] = useState(false);
  const [tplName, setTplName] = useState('');
  const [tplDesc, setTplDesc] = useState('');
  const [saving, setSaving] = useState(false);

  const [openNm, setOpenNm] = useState(false);
  const [nmTitle, setNmTitle] = useState('');
  const [nmDesc, setNmDesc] = useState('');
  const [nmSev, setNmSev] = useState('');

  const [editNm, setEditNm] = useState(null);
  const [editStatus, setEditStatus] = useState('aberto');
  const [editNote, setEditNote] = useState('');
  const [openLists, setOpenLists] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await shJson(
        `/api/safety-huddle/sectors/${sectorId}/templates`,
        unidadeSelecionada
      );
      setSector(data.sector);
      setTemplates((data.items || []).filter((t) => t.active !== false));
      setCanManageForms(!!data.canManageForms);
      const nm = await shJson(
        `/api/safety-huddle/sectors/${sectorId}/near-misses`,
        unidadeSelecionada
      );
      setNearMisses(nm.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [sectorId, unidadeSelecionada]);

  useEffect(() => {
    load();
  }, [load]);

  const nmDist = useMemo(() => {
    const bySeverity = {};
    const byStatus = {};
    for (const n of nearMisses) {
      const sev = (n.severity && String(n.severity).trim()) || '(vazio)';
      const st = n.statusLabel || n.status || 'aberto';
      bySeverity[sev] = (bySeverity[sev] || 0) + 1;
      byStatus[st] = (byStatus[st] || 0) + 1;
    }
    return { bySeverity, byStatus };
  }, [nearMisses]);

  const createTemplate = async () => {
    if (!tplName.trim()) return;
    setSaving(true);
    try {
      const created = await shJson(
        `/api/safety-huddle/sectors/${sectorId}/templates`,
        unidadeSelecionada,
        {
          method: 'POST',
          body: JSON.stringify({ name: tplName.trim(), description: tplDesc.trim() }),
        }
      );
      setOpenTpl(false);
      setTplName('');
      setTplDesc('');
      await load();
      if (created?.id) navigate(`/safety-huddle/setores/${sectorId}/admin/${created.id}`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const createNearMiss = async () => {
    if (!nmTitle.trim()) return;
    setSaving(true);
    try {
      await shJson(`/api/safety-huddle/sectors/${sectorId}/near-misses`, unidadeSelecionada, {
        method: 'POST',
        body: JSON.stringify({
          title: nmTitle.trim(),
          description: nmDesc.trim(),
          severity: nmSev.trim() || null,
        }),
      });
      setOpenNm(false);
      setNmTitle('');
      setNmDesc('');
      setNmSev('');
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteTemplate = async (t) => {
    if (!t?.canDelete) return;
    if (
      !window.confirm(
        `Excluir o formulário «${t.name}»? O histórico de envios é preservado (exclusão lógica).`
      )
    ) {
      return;
    }
    setError('');
    try {
      await shJson(`/api/safety-huddle/templates/${t.id}`, unidadeSelecionada, {
        method: 'DELETE',
      });
      await load();
    } catch (e) {
      setError(e.message);
    }
  };

  const openStatusDialog = (n) => {
    setEditNm(n);
    setEditStatus(n.status || 'aberto');
    setEditNote(n.resolutionNote || '');
  };

  const saveNearMissStatus = async () => {
    if (!editNm?.id) return;
    setSaving(true);
    try {
      const res = await shJson(
        `/api/safety-huddle/near-misses/${editNm.id}`,
        unidadeSelecionada,
        {
          method: 'PATCH',
          body: JSON.stringify({
            status: editStatus,
            resolutionNote: editNote.trim() || null,
          }),
        }
      );
      const updated = res?.item;
      if (updated) {
        setNearMisses((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
      } else {
        await load();
      }
      setEditNm(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/safety-huddle')} sx={{ mb: 2 }}>
        Setores
      </Button>

      <Typography variant="h5" fontWeight={700} color="primary.main">
        {sector?.name || 'Setor'}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {sector?.description || ''}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Formulários" />
        <Tab label={`Quase-falhas (${nearMisses.length})`} />
      </Tabs>

      {tab === 0 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1, flexWrap: 'wrap' }}>
            <Button startIcon={<ListAltIcon />} variant="outlined" onClick={() => setOpenLists(true)}>
              Minhas listas
            </Button>
            {canManageForms && (
              <Button startIcon={<AddIcon />} variant="outlined" onClick={() => setOpenTpl(true)}>
                Novo formulário
              </Button>
            )}
          </Box>
          <List>
            {templates.map((t) => (
              <React.Fragment key={t.id}>
                <ListItemButton
                  onClick={() => navigate(`/safety-huddle/setores/${sectorId}/formularios/${t.id}`)}
                  sx={{ borderRadius: 2, border: '1px solid rgba(25,118,210,0.12)', mb: 1 }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography fontWeight={700}>{t.name}</Typography>
                        <Chip size="small" label={`${t.questionsCount} perguntas`} />
                        <Chip size="small" color="primary" variant="outlined" label={`${t.submissionsCount} envios`} />
                      </Box>
                    }
                    secondary={t.description}
                  />
                  <Box sx={{ display: 'flex', gap: 1, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                    <Button
                      size="small"
                      onClick={() =>
                        navigate(`/safety-huddle/setores/${sectorId}/historico/${t.id}`)
                      }
                    >
                      Histórico
                    </Button>
                    <Button
                      size="small"
                      onClick={() =>
                        navigate(`/safety-huddle/setores/${sectorId}/dashboard/${t.id}`)
                      }
                    >
                      Dashboard
                    </Button>
                    {canManageForms && (
                      <Button
                        size="small"
                        onClick={() => navigate(`/safety-huddle/setores/${sectorId}/admin/${t.id}`)}
                      >
                        Configurar
                      </Button>
                    )}
                    {t.canDelete && (
                      <Button
                        size="small"
                        color="error"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => deleteTemplate(t)}
                      >
                        Excluir
                      </Button>
                    )}
                  </Box>
                </ListItemButton>
              </React.Fragment>
            ))}
            {!templates.length && <Alert severity="info">Nenhum formulário neste setor.</Alert>}
          </List>
        </Box>
      )}

      {tab === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button startIcon={<AddIcon />} variant="contained" onClick={() => setOpenNm(true)}>
              Registrar quase-falha
            </Button>
          </Box>
          {nearMisses.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <NearMissDistributionCharts
                bySeverity={nmDist.bySeverity}
                byStatus={nmDist.byStatus}
                title="Distribuição"
              />
            </Box>
          )}
          <List>
            {nearMisses.map((n) => {
              const days = daysOpenSince(n.createdAt, n.status);
              const openLbl = daysOpenLabel(days);
              return (
                <ListItemButton
                  key={n.id}
                  onClick={() => openStatusDialog(n)}
                  sx={{ borderRadius: 2, border: '1px solid rgba(211,47,47,0.15)', mb: 1 }}
                >
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Typography fontWeight={700}>{n.title}</Typography>
                        {n.severity && <Chip size="small" color="warning" label={n.severity} />}
                        <Chip
                          size="small"
                          color={statusChipColor(n.status)}
                          label={n.statusLabel || n.status || 'aberto'}
                        />
                        {openLbl && (
                          <Chip
                            size="small"
                            variant="outlined"
                            color={days >= 7 ? 'error' : 'default'}
                            label={openLbl}
                          />
                        )}
                      </Box>
                    }
                    secondary={`${n.createdByNome || ''} · ${n.createdAt || ''}${
                      n.description ? ` — ${n.description}` : ''
                    }${n.resolutionNote ? ` · Encerramento: ${n.resolutionNote}` : ''}`}
                  />
                  <Button
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={(e) => {
                      e.stopPropagation();
                      openStatusDialog(n);
                    }}
                  >
                    Status
                  </Button>
                </ListItemButton>
              );
            })}
            {!nearMisses.length && <Alert severity="info">Nenhuma quase-falha registrada.</Alert>}
          </List>
        </Box>
      )}

      <Dialog open={openTpl} onClose={() => setOpenTpl(false)} fullWidth maxWidth="sm">
        <DialogTitle>Novo formulário</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Nome"
            fullWidth
            value={tplName}
            onChange={(e) => setTplName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Descrição"
            fullWidth
            multiline
            minRows={2}
            value={tplDesc}
            onChange={(e) => setTplDesc(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTpl(false)}>Cancelar</Button>
          <Button variant="contained" disabled={saving || !tplName.trim()} onClick={createTemplate}>
            Criar e configurar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openNm} onClose={() => setOpenNm(false)} fullWidth maxWidth="sm">
        <DialogTitle>Quase-falha</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Título"
            fullWidth
            value={nmTitle}
            onChange={(e) => setNmTitle(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Severidade (opcional)"
            fullWidth
            value={nmSev}
            onChange={(e) => setNmSev(e.target.value)}
            placeholder="baixa / média / alta"
          />
          <TextField
            margin="dense"
            label="Descrição"
            fullWidth
            multiline
            minRows={3}
            value={nmDesc}
            onChange={(e) => setNmDesc(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenNm(false)}>Cancelar</Button>
          <Button variant="contained" color="warning" disabled={saving || !nmTitle.trim()} onClick={createNearMiss}>
            Registrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editNm} onClose={() => setEditNm(null)} fullWidth maxWidth="sm">
        <DialogTitle>Atualizar status · {editNm?.title || ''}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Fluxo: aberto → em análise → resolvido
          </Typography>
          <TextField
            select
            margin="dense"
            label="Status"
            fullWidth
            value={editStatus}
            onChange={(e) => setEditStatus(e.target.value)}
          >
            {NM_STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="Observação de encerramento (opcional)"
            fullWidth
            multiline
            minRows={3}
            value={editNote}
            onChange={(e) => setEditNote(e.target.value)}
            helperText={
              editStatus === 'resolvido'
                ? 'Recomendado ao marcar como resolvido'
                : 'Pode registrar nota a qualquer momento'
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditNm(null)}>Cancelar</Button>
          <Button variant="contained" disabled={saving} onClick={saveNearMissStatus}>
            Salvar
          </Button>
        </DialogActions>
      </Dialog>

      <SafetyHuddleOptionListsDialog
        open={openLists}
        onClose={() => setOpenLists(false)}
        sectorId={sectorId}
      />

      <Divider sx={{ my: 2 }} />
    </Box>
  );
}
